import React from 'react';
import {
	AutoComplete,
	Space,
} from 'antd';
// the import does actually work, not sure why its unresolved
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ColumnsType } from 'antd/es/table';
import { DeleteOutlined } from '@ant-design/icons';
import { faPencil } from '@fortawesome/pro-regular-svg-icons';
import {
	CargoContractSides,
	CargoTermTypes,
	CargoUnitLabels,
	CargoUnitTypes,
	CurrencySymbols,
	FreightRateType,
	LaytimeIntervals,
	LaytimeTerms,
} from '@shared/utils/constants';
import { capitalize } from '@shared/utils/string';
import type { GetCargosResponse } from '@api/features/cargos/getCargos';
import type { CargoProps } from '@api/models/cargo';
import type { CargoDetails } from '@api/features/cargos/getCargoDetails';
import { CargoTypeProps } from '@api/models/cargo-type';
import type { FixingCompanyProps } from '@api/models/fixing-company';
import Select from '@client/components/Select';
import Button from '@client/components/Button';
import NumericInput from '@client/components/NumericInput';
import CargoContractSideTag from '@client/screens/cargos/details/utils/CargoContractSideTag';
import getFixingCompanyOptions from '@client/utils/getFixingCompanyOptions';
import styles from '../components/styles/misc.module.css';
import { OnDeleteCargoMutator, OnDeleteCargoReletMutator } from '../context/hooks/useCargoHandlers';
import { ChartererOption } from '../context/hooks/useMiscHandlers';
import { confirmLaytimeTermsChange } from './confirmLaytimeTermsChange';

const SYNCED_RELET_CARGO_FIELDS = new Set<string>([
	'fixingCompanyId',
	'stowageFactor',
	'loadingPorts',
	'dischargePorts',
	'quantity',
	'quantityTolerance',
	'unit',
]);

type ReletCargo = Pick<CargoProps, 'id' | 'contractSide' | 'reletCargoId'>;

export const isSyncedReletCargoField = (field: string) => SYNCED_RELET_CARGO_FIELDS.has(field);

const getVcOutCargo = <T extends ReletCargo>(
	row: T,
	cargos: T[],
) => {
	if (row.contractSide !== CargoContractSides.VC_IN) {
		return row;
	}

	return cargos.find((cargo) => cargo.id === row.reletCargoId) ??
		cargos.find((cargo) => cargo.contractSide === CargoContractSides.VC_OUT) ??
		row;
};

export const getCargoForField = <T extends ReletCargo>(
	row: T,
	cargos: T[],
	field: string,
) => {
	if (
		row.contractSide === CargoContractSides.VC_IN &&
		isSyncedReletCargoField(field)
	) {
		return getVcOutCargo(row, cargos);
	}

	return row;
};

export const updateCargoAndRelet = <T extends ReletCargo, Attributes>({
	sourceCargo,
	cargos,
	attributes,
	onUpdateCargo,
}: {
	sourceCargo: T;
	cargos: T[];
	attributes: Attributes;
	onUpdateCargo: (id: number, attributes: Attributes) => void;
}) => {

	onUpdateCargo(sourceCargo.id, attributes);
	if (sourceCargo.contractSide !== CargoContractSides.VC_OUT) {
		return;
	}

	const linkedVcInCargo = cargos.find((cargo) => (
		cargo.contractSide === CargoContractSides.VC_IN &&
		cargo.reletCargoId === sourceCargo.id
	));

	if (linkedVcInCargo != null) {
		onUpdateCargo(linkedVcInCargo.id, attributes);
	}
};

export const getCargoCardColumns = ({
	onDeleteCargoMutator,
	onDeleteCargoReletMutator,
	onUpdateCargo,
	setEditingCargoId,
	ports,
	isFreightLocked,
	chartererOptions,
	disabled,
	pendingLoadingPortSelections,
	setPendingLoadingPortSelections,
	pendingDischargingPortSelections,
	setDischargingPortPendingSelections,
	cargoTypes,
	fixingCompanies,
	hasCargoRelet,
	cargos,
}: {
	cargos: CargoDetails[];
	onDeleteCargoMutator: OnDeleteCargoMutator;
	onDeleteCargoReletMutator: OnDeleteCargoReletMutator;
	onUpdateCargo: (id: number, attributes: Partial<CargoProps>) => void;
	setEditingCargoId: (id: number) => void;
	ports: {
		label: string;
		value: number;
	}[];
	isFreightLocked: boolean;
	xxl?: boolean;
	chartererOptions: ChartererOption[] | undefined;
	disabled?: boolean;
	pendingLoadingPortSelections: Record<number, number[]>;
	setPendingLoadingPortSelections: React.Dispatch<React.SetStateAction<Record<number, number[]>>>;
	pendingDischargingPortSelections: Record<number, number[]>;
	cargoTypes: CargoTypeProps[];
	fixingCompanies: Array<FixingCompanyProps>;
	setDischargingPortPendingSelections: React.Dispatch<
		React.SetStateAction<Record<number, number[]>>>;
	hasCargoRelet: boolean;
}) => {
	const firstLine: ColumnsType<GetCargosResponse['rows'][number]> = [
		...(hasCargoRelet ? [{
			title: 'Contract',
			dataIndex: 'contractSide',
			key: 'contractSide',
			width: 70,
			render: (value: CargoContractSides) => {
				if (value == null) {
					return null;
				}

				return (
					<CargoContractSideTag contractSide={value} />
				);
			},
		}] : []),
		{
			title: 'Commodity',
			dataIndex: 'type',
			key: 'cargo',
			width: 120,
			render: (value, row) => (
				<AutoComplete
					disabled={disabled}
					autoFocus
					data-testid="cargo-type"
					data-shortcut="cargo-commodity"
					value={value}
					variant="borderless"
					placeholder="Description"
					size="small"
					onChange={(e) => {
						onUpdateCargo(row.id, {
							type: e,
						});
					}}
					onSelect={(v: string) => {
						const cargoType = cargoTypes.find((ct) => ct.name === v);

						onUpdateCargo(row.id, {
							type: v,
							...(cargoType?.stowageFactorCBFT != null && {
								stowageFactor: cargoType.stowageFactorCBFT,
							}),
						});
					}}
					filterOption={(inputValue: string, option?: { label: string }) => (
						option?.label?.toUpperCase().includes(inputValue.toUpperCase()) ?? false
					)}
					options={cargoTypes.map((ct) => ({
						label: ct.name,
						value: ct.name,
					}))}
				/>
			),
		},
		{
			title: 'Stw fac',
			dataIndex: 'stowageFactor',
			key: 'stowage',
			width: 110,
			render: (_, row) => {
				const sourceCargo = getCargoForField(row, cargos, 'stowageFactor');
				const isSyncedField = sourceCargo.id !== row.id;

				return (
					<NumericInput
						disabled={disabled || isSyncedField}
						variant="borderless"
						value={sourceCargo.stowageFactor}
						addonAfter="cbft/ton"
						placeholder="Stowage Factor"
						onChange={(e) => {
							onUpdateCargo(sourceCargo.id, {
								stowageFactor: Number(e),
							});
						}}
					/>
				);
			},
		},
		{
			title: 'Loading',
			dataIndex: 'loadingPorts',
			key: 'loadingPorts',
			width: 200,
			render: (_values, row) => {
				const sourceCargo = getCargoForField(row as CargoDetails, cargos, 'loadingPorts');
				const isSyncedField = sourceCargo.id !== row.id;
				const sourceValues = sourceCargo.loadingPorts ?? [];
				const current = pendingLoadingPortSelections?.[sourceCargo.id] ??
					sourceValues.map((lp: { id: number } | number) => (
						typeof lp === 'object' ? lp.id : lp
					));

				return (
					<Select
						key={`${row.id}-${sourceCargo.id}-${current.join(',')}`}
						noKeyValue
						isRequiredHighlight={current.length === 0}
						disabled={disabled || isSyncedField}
						value={current}
						variant="borderless"
						// @ts-ignore This works because it's feature in rc-select
						showAction="focus"
						mode="multiple"
						placeholder="Loading Ports"
						className={styles.select}
						options={ports}
						onChange={(ids: any) => {
							setPendingLoadingPortSelections((prev) => ({
								...prev,
								[sourceCargo.id]: ids,
							}));
							updateCargoAndRelet({
								sourceCargo,
								cargos,
								attributes: {
									loadingPorts: ids,
								},
								onUpdateCargo,
							});
						}}
					/>
				);
			},
		},
		{
			title: 'Discharging',
			dataIndex: 'dischargePorts',
			key: 'dischargePorts',
			width: 200,
			render: (_values, row) => {
				const sourceCargo = getCargoForField(row, cargos, 'dischargePorts');
				const isSyncedField = sourceCargo.id !== row.id;
				const sourceValues = sourceCargo.dischargePorts ?? [];
				const current =
					pendingDischargingPortSelections?.[sourceCargo.id] ??
					sourceValues.map((dp: { id: number } | number) => (
						typeof dp === 'object' ? dp.id : dp
					));

				return (
					<Select
						key={`${row.id}-${sourceCargo.id}-${current.join(',')}`}
						noKeyValue
						isRequiredHighlight={current.length === 0}
						disabled={disabled || isSyncedField}
						value={current}
						variant="borderless"
						// @ts-ignore This works because it's feature in rc-select
						showAction="focus"
						mode="multiple"
						placeholder="Discharge Ports"
						className={styles.select}
						options={ports}
						onChange={(ids: any) => {
							setDischargingPortPendingSelections((prev: any) => ({
								...prev,
								[sourceCargo.id]: ids,
							}));
							updateCargoAndRelet({
								sourceCargo,
								cargos,
								attributes: {
									dischargePorts: ids,
								},
								onUpdateCargo,
							});
						}}
					/>
				);
			},
		},
		{
			title: 'Qty',
			dataIndex: 'quantity',
			key: 'quantity',
			width: 80,
			render: (_value, row) => {
				const sourceCargo = getCargoForField(row, cargos, 'quantity');
				const isSyncedField = sourceCargo.id !== row.id;
				const value = sourceCargo.quantity;

				return (
					<NumericInput
						isRequiredHighlight={!value}
						disabled={disabled || isSyncedField}
						variant="borderless"
						value={value}
						placeholder="Quantity"
						onChange={(e) => {
							onUpdateCargo(sourceCargo.id, {
								quantity: Number(e),
							});
						}}
					/>
				);
			},
		},
		{
			title: '+/-',
			dataIndex: 'quantityTolerance',
			key: 'quantityTolerance',
			width: 70,
			render: (_value, row) => {
				const sourceCargo = getCargoForField(row, cargos, 'quantityTolerance');
				const isSyncedField = sourceCargo.id !== row.id;
				const value = sourceCargo.quantityTolerance;

				return (
					<NumericInput
						disabled={disabled || isSyncedField}
						variant="borderless"
						value={value}
						addonAfter="%"
						placeholder="Tolerance"
						onChange={(e) => {
							updateCargoAndRelet({
								sourceCargo,
								cargos,
								attributes: {
									quantityTolerance: Number(e),
								},
								onUpdateCargo,
							});
						}}
					/>
				);
			},
		},
		{
			title: 'Unit',
			dataIndex: 'unit',
			key: 'unit',
			width: 100,
			render: (_value, row) => {
				const sourceCargo = getCargoForField(row, cargos, 'unit');
				const isSyncedField = sourceCargo.id !== row.id;
				const value = sourceCargo.unit;

				return (
					<Select
						disabled={disabled || isSyncedField}
						variant="borderless"
						value={value}
						size="small"
						// @ts-ignore This works because it's feature in rc-select
						showAction="focus"
						placeholder="Unit"
						className={styles.select}
						options={Object.keys(CargoUnitTypes).map((unit) => ({
							label: CargoUnitLabels[unit as keyof typeof CargoUnitLabels],
							value: unit,
						}))}
						onChange={(e) => {
							if (e == null) {
								return;
							}

							onUpdateCargo(sourceCargo.id, {
								unit: e,
							});
						}}
						showSearch
					/>
				);
			},
		},
		{
			title: 'Rate',
			dataIndex: 'freightRate',
			key: 'freightRate',
			width: 110,
			render: (value, row) => (
				<NumericInput
					isRequiredHighlight={!row.freightRate || !value}
					variant="borderless"
					value={value}
					addonBefore={CurrencySymbols[row.currency as keyof typeof CurrencySymbols] ??
						row.currency}
					disabled={isFreightLocked || disabled}
					placeholder="Rate"
					onChange={(e) => {
						onUpdateCargo(row.id, {
							freightRate: Number(e),
						});
					}}
				/>
			),
		},
		{
			title: 'Type',
			dataIndex: 'freightType',
			key: 'freightType',
			width: 100,
			render: (value, row) => (
				<Select
					variant="borderless"
					value={value}
					size="small"
					// @ts-ignore This works because it's feature in rc-select
					showAction="focus"
					placeholder="Unit"
					className={styles.select}
					disabled={disabled}
					options={[
						{
							label: 'Lumpsum',
							value: FreightRateType.LUMPSUM,
						},
						{
							label: row.unit != null ? `Per ${CargoUnitLabels[row.unit]}` : 'Per unit',
							value: FreightRateType.PER_UNIT,
						},
					]}
					onChange={(e) => {
						onUpdateCargo(row.id, {
							freightType: e,
						});
					}}
					showSearch
				/>
			),
		},
		{
			title: '',
			dataIndex: '',
			width: 65,
			align: 'right',
			render: (_a, row) => {
				const shouldShowActions =
					!hasCargoRelet ||
					row.contractSide === CargoContractSides.VC_IN;

				if (!shouldShowActions) {
					return null;
				}

				return (
					<Space size={0}>
						<Button
							type="link"
							icon={(
								<FontAwesomeIcon
									data-testid="cargo-modal-btn"
									className={styles.editIcon}
									onClick={() => setEditingCargoId(row.id)}
									icon={faPencil}
								/>
							)}
						/>
						<Button
							onClick={() => hasCargoRelet ?
								onDeleteCargoReletMutator.mutate(row.id) :
								onDeleteCargoMutator.mutate(row.id)
							}
							confirmTitle={
								hasCargoRelet ?
									(
										'Are you sure you want to undo the cargo relet?\n\n' +
										'This will remove the VC-IN cargo and restore the VC-OUT cargo.'
									) :
									'Are you sure you want to remove this cargo from the estimate?'
							}
							type="link"
							danger
							disabled={disabled}
							icon={(<DeleteOutlined />)}
						/>
					</Space>
				);
			},
		},
	];

	const getTimeAllowedColumn = (
		title: string,
		field: 'timeAllowed' | 'timeAllowedLoading' | 'timeAllowedDischarging',
	) => ({
		title,
		dataIndex: field,
		key: field,
		width: 150,
		render: (value: number | null, row: CargoDetails) => (
			<NumericInput
				disabled={disabled}
				variant="borderless"
				value={value == null ? null : Number(value)}
				addonAfter={row.timeAllowedInterval}
				placeholder="Time allowed"
				onChange={(e) => onUpdateCargo(row.id, { [field]: e })}
				data-testid={field}
			/>
		),
	});

	const getSecondLine = (cargo: CargoDetails): ColumnsType<CargoDetails> => [
		{
			title: 'Counterparty',
			dataIndex: 'charterer',
			width: 130,
			render: (value: number, row: CargoDetails) => (
				<Select
					noKeyValue
					isRequiredHighlight={!row.charterer}
					disabled={disabled}
					variant="borderless"
					value={value}
					size="small"
					// @ts-ignore rc-select feature
					showAction="focus"
					placeholder={row.contractSide === CargoContractSides.VC_IN ? 'Owner' : 'Charterer'}
					className={styles.select}
					options={chartererOptions}
					onChange={(c) => onUpdateCargo(row.id, { charterer: c })}
					showSearch
				/>
			),
		},
		{
			title: 'Terms',
			dataIndex: 'terms',
			key: 'terms',
			width: 100,
			render: (value, row) => (
				<Select
					disabled={disabled}
					variant="borderless"
					value={value}
					size="small"
					// @ts-ignore This works because it's feature in rc-select
					showAction="focus"
					placeholder="Terms"
					className={styles.select}
					options={Object.keys(CargoTermTypes).map((terms) => ({
						label: terms,
						value: terms,
					}))}
					onChange={(e) => {
						onUpdateCargo(row.id, {
							terms: e,
						});
					}}
					showSearch
				/>
			),
		},
		{
			title: 'Ad. Com',
			dataIndex: 'addressCommission',
			key: 'addressCommission',
			width: 60,
			render: (value, row) => (
				<NumericInput
					isRequiredHighlight={row.addressCommission == null || value == null}
					disabled={disabled}
					className={styles.percentage}
					variant="borderless"
					value={value}
					addonAfter="%"
					placeholder="Ad. Com."
					onChange={(e) => {
						onUpdateCargo(row.id, {
							addressCommission: Number(e),
						});
					}}
				/>
			),
		},
		{
			title: 'Br. Com',
			dataIndex: 'brokerCommission',
			key: 'brokerCommission',
			width: 60,
			render: (value, row) => (
				<NumericInput
					disabled={disabled || row?.brokers?.length > 1}
					className={styles.percentage}
					variant="borderless"
					value={value}
					addonAfter="%"
					placeholder="Br. Com."
					onChange={(e) => {
						onUpdateCargo(row.id, {
							brokerCommission: Number(e),
						});
					}}
				/>
			),
		},
		{
			title: 'Laytime type',
			dataIndex: 'laytimeTerms',
			key: 'laytimeTerms',
			width: 130,
			render: (value, row) => (
				<Select
					disabled={disabled}
					variant="borderless"
					value={value}
					size="small"
					// @ts-ignore This works because it's feature in rc-select
					showAction="focus"
					placeholder="Laytime type"
					className={styles.select}
					options={Object.values(LaytimeTerms).map((terms) => ({
						label: terms,
						value: terms,
					}))}
					onChange={(laytimeTerms) => {
						if (laytimeTerms == null || laytimeTerms === row.laytimeTerms) {
							return;
						}

						confirmLaytimeTermsChange(row, () => onUpdateCargo(row.id, { laytimeTerms }));
					}}
					data-testid="laytimeTerms"
				/>
			),
		},
		...(cargo.laytimeTerms === LaytimeTerms.REVERSIBLE || cargo.laytimeTerms === LaytimeTerms.AVERAGED ? [{
			title: 'Time allowed interval',
			dataIndex: 'timeAllowedInterval',
			key: 'timeAllowedInterval',
			width: 130,
			render: (value: LaytimeIntervals, row: CargoDetails) => (
				<Select
					disabled={disabled}
					variant="borderless"
					value={value}
					size="small"
					// @ts-ignore This works because it's feature in rc-select
					showAction="focus"
					className={styles.select}
					options={Object.values(LaytimeIntervals).map((interval) => ({
						label: capitalize(interval),
						value: interval,
					}))}
					onChange={(timeAllowedInterval) => onUpdateCargo(row.id, {
						timeAllowedInterval: timeAllowedInterval ?? undefined,
					})}
					data-testid="timeAllowedInterval"
				/>
			),
		}] : []),
		...(cargo.laytimeTerms === LaytimeTerms.REVERSIBLE ? [
			getTimeAllowedColumn('Total time allowed', 'timeAllowed'),
		] : []),
		...(cargo.laytimeTerms === LaytimeTerms.AVERAGED ? [
			getTimeAllowedColumn('Total time allowed loading', 'timeAllowedLoading'),
			getTimeAllowedColumn('Total time allowed discharging', 'timeAllowedDischarging'),
		] : []),
		{
			title: 'Fixing Company',
			dataIndex: 'fixingCompanyId',
			width: 130,
			render: (_, row: CargoDetails) => {
				const sourceCargo = getCargoForField(row, cargos, 'fixingCompanyId');
				const isSyncedField = sourceCargo.id !== row.id;

				return (
					<Select
						data-testid="cargo-fixing-company"
						noKeyValue
						isRequiredHighlight={!sourceCargo.fixingCompanyId}
						disabled={disabled || isSyncedField}
						variant="borderless"
						value={sourceCargo.fixingCompanyId}
						size="small"
						placeholder="Fixing Company"
						className={styles.select}
						options={getFixingCompanyOptions(fixingCompanies, sourceCargo.fixingCompanyId)}
						onChange={(fixingCompanyId) => onUpdateCargo(sourceCargo.id, { fixingCompanyId })}
						showSearch
					/>
				);
			},
		},
	];

	return { firstLine, getSecondLine };
};
